import _, { get, isNil, sortBy, Dictionary } from 'lodash';
import { SettingsByKey } from '../../../services/Settings';
import { Dimension } from '../../../pivot/Dimension';
import { Group } from '../../../pivot/Group';
import { HierarchyMember } from '../../../pivot/Member';
import { ServerScope, HierData, ServerScopeResponse, isScopeNotReady } from '../../../state/scope/Scope.types';
import { configItemToSimpleFavorite } from '../../../services/Favorites';
import { ConfigGroup, ConfigItem } from './PivotConfigurator.types';
import {
  METRICS,
  SINGLE_VERSION,
  ROWS,
  COLUMNS,
  VARIANCE_VERSION,
  PERCENT_TO_TOTAL_VERSION,
  PERCENT_TO_GRAND_TOTAL_VERSION,
} from '../../../utils/Domain/Constants';
import { PERCENTAGE_VARTYPE, DELTA_VARTYPE } from 'src/services/Pivot.types';
import { PRODUCT } from 'src/utils/Domain/Constants';
import { InlineTree, Tree } from 'src/utils/types';
import { z } from 'zod';
import { RequiredDimension } from 'src/space';
import { MEMBER_TYPES } from 'src/utils/interface/Constants.interface';

const zDimensionItem = z.object({
  dimension: z.string(),
  /** `items` here generally reprsents levels in a favorite, or type-prefixed strings of member: or level: */
  items: z.array(z.string()),
  /** Used for alternate hierarchies.  If not present the 'primary' hierarchy for the dimensions is used */
  hierarchy: z.string().optional(),
});
export interface DimensionItem extends z.infer<typeof zDimensionItem> {}

export const zPivotOptions = z.object({
  [ROWS]: z.array(zDimensionItem),
  [COLUMNS]: z.array(zDimensionItem),
  name: z.string().optional(),
  groupBy: z
    .object({
      dimension: z.string(),
      item: z.string(),
    })
    .optional(),
  /**
   * Selected top-level member filters. Saved per-dimension like rows/columns so that both
   * the member selection AND its level are preserved, letting us fall back to the level when
   * a saved member is not present in the current scope.
   */
  filters: z.array(zDimensionItem).optional(),
  readOnly: z.boolean(),
});

export interface PivotOptions {
  name?: string;
  [ROWS]: DimensionItem[];
  [COLUMNS]: DimensionItem[];
  groupBy?: {
    dimension: string;
    item: string;
  };
  /**
   * Selected top-level member filters. Saved per-dimension like rows/columns so that both
   * the member selection AND its level are preserved, letting us fall back to the level when
   * a saved member is not present in the current scope.
   */
  filters?: DimensionItem[];
}

export interface EffectiveGroups {
  available: ConfigGroup[];
  row: ConfigGroup[];
  column: ConfigGroup[];
  /**
   * The top-level member filter selection reconciled against `available`: any selection saved on
   * the pivot options is restored, dimensions that are newly available pick up a default, and
   * dimensions that are no longer available are dropped. Empty when no `mainConfig` is supplied.
   */
  selectedGroup: Group[];
}

/**
 * PRODUCT favorite safety on scope change (should work fine with existing favorites too):
 * - if any saved member:* exists in current scope tree => keep items as-is
 * - else if we have level:* tokens => keep levels only & expand whole levels
 * - else fallback to first top-level product member so pivot isnt empty
 */
function patchProductItemsForScope(rawItems: string[], productRoot: ConfigItem): string[] {
  const levelTokens = rawItems.filter((x) => x.startsWith('level:'));
  const memberTokens = rawItems.filter((x) => x.startsWith('member:'));

  if (memberTokens.length === 0) return rawItems;

  let hasAnyFavoriteMemberInTree = false;

  const searchTreeForFavoriteMember = (node: ConfigItem) => {
    if (hasAnyFavoriteMemberInTree) return;

    hasAnyFavoriteMemberInTree = _.some(memberTokens, (t) => t.slice('member:'.length) === node.id);

    if (hasAnyFavoriteMemberInTree) return;

    node.children?.forEach(searchTreeForFavoriteMember);
  };

  searchTreeForFavoriteMember(productRoot);

  if (hasAnyFavoriteMemberInTree) return rawItems;

  // no members => expand levels
  if (levelTokens.length > 0) return levelTokens;

  // last resort safety
  const firstChildId = productRoot.children?.[0]?.id;
  return firstChildId ? [`member:${firstChildId}`] : rawItems;
}

export function getEffeciveGroups(
  superGroup: ConfigGroup[],
  pivotOptions: PivotOptions,
  mainConfig?: ServerScopeResponse,
): EffectiveGroups {
  const rowDimensions = pivotOptions.rows.map((entry) => entry.dimension);
  const colDimensions = pivotOptions.columns.map((entry) => entry.dimension);

  const rowMemberInfo: ConfigGroup[] = rowDimensions.map((dim) => {
    return superGroup.find((sg) => sg.dimension === dim)!;
  });
  const colMemberInfo: ConfigGroup[] = colDimensions.map((dim) => {
    return superGroup.find((sg) => sg.dimension === dim)!;
  });
  const availableMemberInfo = superGroup.filter((sg) => {
    //everything else
    const isInRows = rowMemberInfo.findIndex((r) => r.dimension === sg.dimension) !== -1;
    const isInCols = colMemberInfo.findIndex((c) => c.dimension === sg.dimension) !== -1;

    return !isInRows && !isInCols;
  });

  // check and set disabled bool so configurator can match the state of the grid
  colMemberInfo.forEach((colMember) => {
    setConfigItemDisabled(colMember, pivotOptions);
    const sortByList = ['sortIndex', 'group'];
    if (colMember.id === METRICS) {
      sortByList.push('name');
    } else if (colMember.id === PRODUCT) {
      sortByList.push('id');
    }
    colMember.children = recursiveSort(colMember.children!, sortByList);
  });
  // don't forget to do the rows
  rowMemberInfo.forEach((rowMember) => {
    setConfigItemDisabled(rowMember, pivotOptions);
    const sortByList = ['sortIndex', 'group'];
    if (rowMember.id === METRICS) {
      sortByList.push('name');
    } else if (rowMember.id === PRODUCT) {
      sortByList.push('id');
    }
    rowMember.children = recursiveSort(rowMember.children!, sortByList);
  });

  // Restore the saved top-level member filters, then reconcile them against what's actually
  // available now: dimensions moved into rows/columns drop out, newly available ones get a default.
  const savedSelection = buildSelectedGroupFromFilters(mainConfig, availableMemberInfo, pivotOptions.filters ?? []);

  return {
    available: availableMemberInfo,
    row: rowMemberInfo,
    column: colMemberInfo,
    selectedGroup: mergeSelectedGroups(mainConfig, availableMemberInfo, savedSelection),
  };
}

function recursiveSort(items: ConfigItem[], sortList: string[]): ConfigItem[] {
  items.forEach((itm) => {
    if (itm.children && itm.children.length > 0) {
      itm.children = recursiveSort(itm.children, sortList);
    }
  });
  return sortBy(items, sortList);
}

export function transformPayload<A>(vObj: Tree<A>): InlineTree<A> {
  const memberInfo = vObj.v;
  const children = vObj.children;
  return {
    ...memberInfo,
    children: children.map(transformPayload),
  };
}

export function getAvailableListing(
  mainConfig: ServerScope,
  settings: SettingsByKey,
  altHier?: DimensionItem[],
): ConfigGroup[] {
  const memberTrees = mainConfig.memberTrees;
  const groups: ConfigGroup[] = [];

  for (const dimId of Object.keys(memberTrees)) {
    const dimensionId = dimId as RequiredDimension;
    const maybeAlternateHier = altHier?.find((d) => d.dimension === dimensionId);
    if (maybeAlternateHier && maybeAlternateHier.hierarchy) {
      const vObj = getAlternateHierarchy(memberTrees[dimensionId], maybeAlternateHier.hierarchy);
      groups.push({
        id: dimensionId,
        name: dimensionId,
        dimension: dimensionId,
        hierarchy: maybeAlternateHier.hierarchy,
        // @ts-ignore
        children: vObj.map(transformPayload),
      });
    } else if (memberTrees[dimensionId]) {
      const vObj = getPrimary(memberTrees[dimensionId]);
      groups.push({
        id: dimensionId,
        name: dimensionId,
        dimension: dimensionId,
        // @ts-ignore
        children: vObj.map(transformPayload),
      });
    }
  }

  const metrics = mainConfig.metrics || [];
  groups.push({
    id: 'metrics',
    name: 'metrics',
    dimension: 'metrics',
    children: metrics.map((metric) => {
      const fieldId = metric.id;

      return {
        name: _.defaultTo(metric.name, fieldId),
        id: fieldId,
        format: metric.formats,
        group: _.defaultTo(get(metric, 'group'), undefined) as string | undefined,
        alwaysHidden: metric.hidden,
        type: 'metric',
        children: [],
      };
    }),
  });

  // TODO move this to settings? Maybe another client would want a different label
  const varTypeToName = {
    [DELTA_VARTYPE]: 'Diff',
    [PERCENTAGE_VARTYPE]: '%',
  };
  const altSimpleVersionLookup = {
    [PERCENT_TO_TOTAL_VERSION]: ' % to Total',
    [PERCENT_TO_GRAND_TOTAL_VERSION]: ' % to Grand Total',
  };
  const revisions = mainConfig.revisions;
  groups.push({
    id: 'revisions',
    name: 'Versions',
    dimension: 'revisions',
    children: revisions
      .filter(
        (v) =>
          v.type === SINGLE_VERSION ||
          v.type === VARIANCE_VERSION ||
          v.type === PERCENT_TO_TOTAL_VERSION ||
          v.type === PERCENT_TO_GRAND_TOTAL_VERSION,
      )
      .map((vers) => {
        let id = vers.version;
        const verLookup = settings[`revision.${id}.display`];
        let verName = verLookup ? verLookup.value : id.toLocaleUpperCase();

        if (vers.type === VARIANCE_VERSION) {
          // variance versions have a different shape
          const againstLookup = settings[`revision.${vers.against}.display`];
          const againstName = againstLookup ? againstLookup.value : vers.against.toLocaleUpperCase();
          return {
            name: `${verName} to ${againstName} ${varTypeToName[vers.varType]}`,
            id: `${vers.version}||${vers.against}||${vers.varType}`,
            varType: vers.varType,
            type: vers.type,
            alwaysHidden: vers.hidden,
            children: [],
          };
        }
        verName = vers.type === SINGLE_VERSION ? verName : `${verName}${altSimpleVersionLookup[vers.type]}`;
        id = vers.type === SINGLE_VERSION ? vers.version : `${vers.version}${vers.type}`;

        return {
          id,
          name: verName,
          children: [],
          type: vers.type,
          alwaysHidden: vers.hidden,
        };
      }),
  });
  return groups;
}

export function setConfigItemDisabled(configItem: ConfigItem, faveOpts: PivotOptions): void {
  const dimension = {
    id: configItem.id,
    name: configItem.id,
    description: configItem.id,
  };
  function checkChildren(cm: ConfigItem, dim: Dimension, parent: ConfigItem): boolean {
    let isDisabled = true;
    const dimColCfg = _.find(faveOpts.columns, (vp) => vp.dimension === dim.id);
    const dimRowCfg = _.find(faveOpts.rows, (vp) => vp.dimension === dim.id);

    const dimCfg = !isNil(dimColCfg) ? dimColCfg : dimRowCfg;

    if (isNil(dimCfg)) {
      return isDisabled;
    }

    let items = dimCfg.items;

    // patch based on current scopes product tree
    if (dim.id === PRODUCT) {
      items = patchProductItemsForScope(items, configItem);
    }
    items.forEach((item, i) => {
      if (`level:${cm.level}` === item && isDisabled) {
        const parentHasAny = parent.children?.some((c) => items.includes(`member:${c.id}`));
        if (parentHasAny || cm.children?.some((c) => items.includes(`member:${c.id}`))) {
          isDisabled = true;
          cm.disabled = true;
        } else {
          isDisabled = false;
          cm.disabled = isDisabled;
        }
        cm.sortIndex = items.findIndex((itm) => itm === `level:${cm.level}`);
      } else if (`member:${cm.id}` === item) {
        isDisabled = false;
        cm.disabled = isDisabled;
        cm.sortIndex = items.findIndex((itm) => itm === `member:${cm.id}`);
      } else if (cm.id === item) {
        isDisabled = false;
        cm.disabled = isDisabled;
        cm.sortIndex = items.findIndex((itm) => itm === `${cm.id}`);
      } else {
        cm.disabled = isDisabled;
      }
    });
    if (cm.children) {
      cm.children.map((child) => checkChildren(child, dim, cm));
    }

    return isDisabled;
  }

  configItem.disabled = checkChildren(configItem, dimension, configItem);
}

export function getGroupFromConfigItem(
  configItem: ConfigItem,
  faveOpts: PivotOptions,
  levelsMap: Dictionary<
    HierData<
      {
        id: string;
        name: string;
        description?: string;
      }[]
    >
  >,
): Group {
  const name: string = configItem.name || configItem.id;
  const dimension = {
    id: configItem.id,
    name,
    description: configItem.id,
    hierarchy: configItem.hierarchy,
  };
  const level = {
    name: `${name} Level`,
    description: configItem.id,
    ...levelsMap[configItem.level!],
    id: `${configItem.id}Level`,
    dimension,
  };

  const root: SortedHierarchyMember = {
    level,
    id: configItem.id,
    name: configItem.name || '',
    description: configItem.description || '',
    isHidden: true,
    areChildrenColumnsVisible: true,
  };
  interface SortedHierarchyMember extends HierarchyMember {
    sortValue?: number;
    alwaysHidden?: boolean;
    children?: SortedHierarchyMember[];
  }

  // build a memberId -> levelId lookup for this dimension
  const memberToLevel: Record<string, string> = {};

  function indexMemberToLevel(node: ConfigItem): void {
    node.children?.forEach((child) => {
      if (child.level) memberToLevel[child.id] = child.level;
      indexMemberToLevel(child);
    });
  }
  indexMemberToLevel(configItem);

  // gather the favorite items for this dimension once
  const dimRowCfg = _.find(faveOpts.rows, (vp) => vp.dimension === dimension.id);
  const dimColCfg = _.find(faveOpts.columns, (vp) => vp.dimension === dimension.id);

  /** slice to create a local copy so we can safely patch for current scope only **/
  let dimItems: string[] = (dimRowCfg?.items ?? dimColCfg?.items ?? []).slice();

  if (dimension.id === PRODUCT) {
    // patch based on current scopes product tree
    dimItems = patchProductItemsForScope(dimItems, configItem);
  }

  // Helper: does this level have any specific members selected ?
  function levelHasSpecificMembers(levelId?: string): boolean {
    if (!levelId) return false;
    return dimItems.some(
      (item) => item.startsWith('member:') && memberToLevel[item.slice('member:'.length)] === levelId,
    );
  }

  function getChildren(hm: SortedHierarchyMember, cm: ConfigItem, dim: Dimension): SortedHierarchyMember {
    const childLevel = {
      ...cm,
      name: `${cm.name || cm.id} Level`,
      description: cm.id,
      alwaysHidden: cm.alwaysHidden,
      ...levelsMap[cm.level!],
      id: `${cm.id}Level`,
      dimension: dim,
    };

    const newRoot: SortedHierarchyMember = {
      id: cm.id,
      name: cm.name || '',
      description: cm.name || '',
      alwaysHidden: cm.alwaysHidden,
      level: childLevel,
      parent: hm,
      areChildrenColumnsVisible: true,
    };
    if (!isNil(cm.format)) {
      newRoot.format = cm.format;
    }
    if (!isNil(cm.group)) {
      newRoot.group = cm.group;
    }
    if (!isNil(cm.varType)) {
      newRoot.varType = cm.varType;
    }
    if (!isNil(cm.type)) {
      newRoot.type = cm.type as MEMBER_TYPES;
    }

    let isHidden = true;
    [ROWS, COLUMNS].forEach((rcType) => {
      const dimCfg = _.find(faveOpts[rcType], (vp) => vp.dimension === dim.id);
      if (!dimCfg || !dimCfg.items) {
        return;
      }
      // use the dimItems that are possibly patched above & not the raw favorite
      // selection that is likely invalid on scope change (if department is changed)
      // dimItems guarantees product is never empty
      const items: string[] = dimItems;

      items.forEach((item, i) => {
        if (`member:${cm.id}` === item || cm.id === item) {
          // explicit member selection first
          isHidden = false;
          newRoot.sortValue = i;
        } else if (`level:${cm.level}` === item) {
          // only expand the whole level if there are NO specific members at this level
          if (!levelHasSpecificMembers(cm.level)) {
            isHidden = false;
          }
        }
      });
    });
    let newChildren: SortedHierarchyMember[] = [];
    if (cm.children) {
      newChildren = cm.children.map((child) => getChildren(newRoot, child, dim));
    }

    newChildren = _.sortBy(newChildren, (child) => child.sortValue);
    newRoot.isHidden = isHidden;
    newRoot.children = newChildren;
    return newRoot;
  }
  let rootChildren: SortedHierarchyMember[] = [];
  if (configItem.children) {
    rootChildren = configItem.children.map((child) => getChildren(root, child, dimension));
  }

  rootChildren = _.sortBy(rootChildren, (child) => child.sortValue);
  root.children = rootChildren;
  return new Group(root, true);
}

export function getUnFilteredGroupFromConfigItem(
  row: ConfigItem,
  levelsMap: Dictionary<
    HierData<
      {
        id: string;
        name: string;
        description?: string;
      }[]
    >
  >,
): Group {
  const name = row.name || row.id;
  const dimension = {
    id: row.id,
    name,
    description: row.id,
    hierarchy: row.hierarchy,
  };
  const level = {
    name: `${name} Level`,
    description: row.id,
    ...levelsMap[row.level!],
    id: `${row.id}Level`,
    dimension,
  };

  const root: HierarchyMember = {
    level,
    id: row.id,
    name: row.name || '',
    description: row.description || '',
    isHidden: true,
    areChildrenColumnsVisible: true,
  };

  function getChildren(hm: HierarchyMember, cm: ConfigItem, dim: Dimension): HierarchyMember {
    const childLevel = {
      name: `${cm.name || cm.id} Level`,
      description: cm.id,
      alwaysHidden: cm.alwaysHidden,
      ...levelsMap[cm.level!],
      id: `${cm.id}Level`,
      dimension: dim,
    };
    const newRoot: HierarchyMember = {
      id: cm.id,
      name: cm.name || '',
      description: cm.name || '',
      type: (cm.type as MEMBER_TYPES) || undefined,
      level: childLevel,
      parent: hm,
      isHidden: !!cm.disabled,
      areChildrenColumnsVisible: true,
    };
    if (!isNil(cm.format)) {
      newRoot.format = cm.format;
    }
    if (!isNil(cm.varType)) {
      newRoot.varType = cm.varType;
    }
    if (!isNil(cm.type)) {
      newRoot.type = cm.type as MEMBER_TYPES;
    }
    if (cm.children) {
      newRoot.children = cm.children.map((child) => getChildren(newRoot, child, dim));
    }

    return newRoot;
  }

  root.children = row.children!.map((child) => getChildren(root, child, dimension));
  return new Group(root, true);
}

export function makeErrorLog(message: string) {
  return (e: unknown) => {
    // eslint-disable-next-line no-console
    console.error(message, ': ', e);
    const err = e as Error;
    return Promise.reject(err);
  };
}

export function getPrimaryData<T>(hiers: readonly HierData<T>[]): HierData<T> {
  const primary = hiers.find((p) => p.primary);
  if (!primary) {
    // Denver said it was Ok
    throw new Error('broken data has no primary');
  }
  return primary;
}

export function getPrimary<T>(hiers: HierData<T>[]): T {
  return getPrimaryData(hiers).data;
}

export function getPrimaryId<T>(hiers: readonly HierData<T>[]): string {
  return getPrimaryData(hiers).id;
}

export function getAlternateHierarchyData<T>(hiers: readonly HierData<T>[], hierId: string): HierData<T> {
  const altHier = hiers.find((p) => p.id === hierId);
  if (!altHier) {
    // Denver said it was Ok
    throw new Error('Requested alt hier could not be found');
  }
  return altHier;
}

export function getAlternateHierarchy<T>(hiers: HierData<T>[], hierId: string): T {
  return getAlternateHierarchyData<T>(hiers, hierId).data;
}

/**
 * Find the dimension (and its level) that a member id belongs to within the available groups.
 */
export function findAvailableMember(
  availableGroups: ConfigGroup[],
  memberId: string,
): { dimId: string; level?: string } | undefined {
  for (const group of availableGroups) {
    let result: { dimId: string; level?: string } | undefined;
    const walk = (item: ConfigItem): boolean => {
      if (item.id === memberId) {
        result = { dimId: group.dimension || group.id, level: item.level };
        return true;
      }
      return !!item.children?.some(walk);
    };
    // `some()` here short-circuits when it finds one match,
    // then we return right after, so we don't have to walk the whole tree
    group.children?.some(walk);
    if (result) {
      return result;
    }
  }
  return undefined;
}

/**
 * Clone a dimension's available ConfigGroup with everything disabled (hidden) except the
 * explicitly selected members. Used both to build filter Groups and to serialize favorites.
 */
export function buildFilterConfigGroup(
  availableGroups: ConfigGroup[],
  dimId: string,
  memberIds: Set<string>,
): ConfigGroup | undefined {
  if (!memberIds.size) {
    return undefined;
  }
  const src = availableGroups.find((g) => g.dimension === dimId || g.id === dimId);
  if (!src) {
    return undefined;
  }
  const cfg = _.cloneDeep(src);
  const markDisabled = (item: ConfigItem) => {
    item.disabled = !memberIds.has(item.id);
    item.children?.forEach(markDisabled);
  };
  cfg.children?.forEach(markDisabled);
  return cfg;
}

/** Build filter Groups for a dimension whose visible members are exactly the selected ids. */
export function buildFilterGroups(
  mainConfig: ServerScopeResponse | undefined,
  availableGroups: ConfigGroup[],
  dimId: string,
  memberIds: Set<string>,
): Group[] {
  if (!mainConfig || isScopeNotReady(mainConfig)) {
    return [];
  }
  const cfg = buildFilterConfigGroup(availableGroups, dimId, memberIds);
  if (!cfg) {
    return [];
  }
  const levelsMap = _.keyBy(_.flatMap(mainConfig.levels), 'id');
  return [getUnFilteredGroupFromConfigItem(cfg, levelsMap)];
}

/** Build the default selection (first member of every available group). */
export function buildDefaultSelectedGroup(
  mainConfig: ServerScopeResponse | undefined,
  availableGroups: ConfigGroup[],
): Group[] {
  return _.flatMap(availableGroups, (group) => {
    const firstMemberId = group.children?.[0]?.id;
    return firstMemberId
      ? buildFilterGroups(mainConfig, availableGroups, group.dimension || group.id, new Set([firstMemberId]))
      : [];
  });
}

/**
 * Reconcile an existing top-level member selection against a new set of available groups.
 *
 * Dimensions that are still available keep the selection they already had; dimensions that just
 * became available (e.g. dragged out of rows/columns) get the default selection (first member);
 * dimensions that are no longer available are dropped. This replaces the older all-or-nothing
 * `isSelectedGroupValid` check, which threw away every selection as soon as one dimension changed.
 */
export function mergeSelectedGroups(
  mainConfig: ServerScopeResponse | undefined,
  newAvailableGroup: ConfigGroup[],
  selectedGroup: Group[],
): Group[] {
  const availableDimIds = new Set(newAvailableGroup.map((g) => g.dimension || g.id));

  /** Previous selections whose dimension is still available */
  const kept = selectedGroup.filter((g) => availableDimIds.has(g.dimension.id));
  const keptDimIds = new Set(kept.map((g) => g.dimension.id));

  /** Newly available dimensions, which need their defaults calculated */
  const addedGroups = newAvailableGroup.filter((g) => !keptDimIds.has(g.dimension || g.id));

  return [...kept, ...buildDefaultSelectedGroup(mainConfig, addedGroups)];
}

/**
 * Serialize the current selection into per-dimension favorites (like rows/columns), preserving
 * both the selected members AND their levels via configItemToSimpleFavorite, so that on reload we
 * can fall back to the level when a saved member is no longer present in the scope.
 */
export function buildFilterFavorites(availableGroups: ConfigGroup[], selectedGroup: Group[]): DimensionItem[] {
  const availableGroupIds = availableGroups.map((g) => g.dimension);
  return _.flatMap(selectedGroup, (g) => {
    const dimId = g.dimension.id;
    const memberIds = new Set(g.getVisible().map((f) => f.member.id));
    const cfg = availableGroupIds.includes(dimId) && buildFilterConfigGroup(availableGroups, dimId, memberIds);
    return cfg ? [configItemToSimpleFavorite(cfg)] : [];
  });
}

/**
 * Find the first member in a dimension's available ConfigGroup whose level matches one of the
 * given (favorite) level ids, checking the levels in order. Used to recover a sensible selection
 * when a favorite's saved member no longer exists in the current scope.
 */
export function firstMemberOfMatchingLevel(src: ConfigGroup, levelIds: string[]): string | undefined {
  for (const levelId of levelIds) {
    let found: string | undefined;
    const walk = (item: ConfigItem): boolean => {
      if (item.level === levelId) {
        found = item.id;
        return true;
      }
      return !!item.children?.some(walk);
    };
    src.children?.some(walk);
    if (found) {
      return found;
    }
  }
  return undefined;
}

/**
 * Rebuild the selection Groups from the per-dimension filters saved on a favorite. When every
 * saved member for a dimension still exists in the current scope we restore them exactly;
 * otherwise (a member selection isn't in scope) we fall back to the first member of the first
 * saved level that matches a level in the current scope.
 */
export function buildSelectedGroupFromFilters(
  mainConfig: ServerScopeResponse | undefined,
  availableGroups: ConfigGroup[],
  filters: DimensionItem[],
): Group[] {
  return _.flatMap(filters, (filter) => {
    const src = availableGroups.find((g) => g.dimension === filter.dimension || g.id === filter.dimension);
    if (!src) {
      return [];
    }
    const memberIds = filter.items.filter((i) => i.startsWith('member:')).map((i) => i.slice('member:'.length));
    const levelIds = filter.items.filter((i) => i.startsWith('level:')).map((i) => i.slice('level:'.length));

    // collect every member id present in the current scope for this dimension
    const present = new Set<string>();
    const collect = (item: ConfigItem) => {
      present.add(item.id);
      item.children?.forEach(collect);
    };
    src.children?.forEach(collect);

    // restore the exact members only when all of them still exist in the current scope
    const allInScope = memberIds.length > 0 && memberIds.every((id) => present.has(id));
    if (allInScope) {
      return buildFilterGroups(mainConfig, availableGroups, filter.dimension, new Set(memberIds));
    }

    // a saved member isn't in scope: select the first member of the first matching saved level
    const fallbackId = firstMemberOfMatchingLevel(src, levelIds) ?? src.children?.[0]?.id;
    return fallbackId ? buildFilterGroups(mainConfig, availableGroups, filter.dimension, new Set([fallbackId])) : [];
  });
}

/**
 * A selection is valid only when it covers every available dimension. It's invalid
 * when there are available groups but no selection at all (or a selection with no
 * available groups), or when any available dimension is missing from the current
 * selection — in either case the selection must be rebuilt from defaults.
 */
export function isSelectedGroupValid(selectedGroup: Group[], newAvailableGroup: ConfigGroup[]): boolean {
  if (_.isEmpty(newAvailableGroup) && !_.isEmpty(selectedGroup)) {
    return false;
  }
  if (_.isEmpty(selectedGroup) && !_.isEmpty(newAvailableGroup)) {
    return false;
  }
  const selectedDims = new Set(selectedGroup.map((g) => g.dimension.id));
  return newAvailableGroup.every((g) => selectedDims.has(g.dimension || g.id));
}
